import React from 'react';

// You can replace this with an import if you have a logo image file
const OgLogo = () => (
  <div className="flex items-center space-x-3 mb-4">
    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-400 rounded-full flex items-center justify-center">
      <span className="text-white font-bold text-2xl">🏀</span>
    </div>
    <div className="flex flex-col">
      <span className="font-orbitron font-bold text-basketball-blue-500 text-lg">OG Basketball</span>
      <span className="text-xs text-gray-500">Tanzania Club</span>
    </div>
  </div>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="font-bold text-base mt-6 mb-2 border-b border-gray-300 pb-1 uppercase tracking-wide">{children}</h2>
);

const LabelValue = ({ label, value, bold = false }: { label: string; value?: string | number; bold?: boolean }) => (
  <div className="flex mb-1">
    <div className="w-56 text-gray-700 font-medium">{label}</div>
    <div className={bold ? 'font-bold' : ''}>{value || <span className="text-gray-400">-</span>}</div>
  </div>
);

function getInitials(first: string, last: string) {
  if (!first || !last) return '';
  return `${first[0].toUpperCase()}.${last.charAt(0).toUpperCase() + last.slice(1)}`;
}

export interface MemberFormTemplateProps {
  data: any; // Use a more specific type if available
  showClubSection?: boolean; // For Section F (Club Use Only)
}

const MemberFormTemplate: React.FC<MemberFormTemplateProps> = ({ data, showClubSection = true }) => {
  const memberName = `${data.first_name || ''} ${data.last_name || ''}`.trim();
  const photoUrl = data.photo_url || data.photo; // Use either photo_url or photo

  return (
    <div className="bg-white p-8 max-w-[800px] mx-auto shadow-md rounded text-[15px] font-sans" style={{ width: '210mm', minHeight: '297mm' }}>
      {/* Header */}
      <div className="flex flex-col items-center mb-6">
        <OgLogo />
        <div className="text-center mt-2">
          <div className="font-bold text-lg">Old Guards Basketball Club</div>
          <div className="font-semibold text-sm">Membership Application Form</div>
        </div>
      </div>

      {/* Member Photo */}
      {photoUrl && (
        <div className="flex justify-center mb-6">
          <img 
            src={photoUrl}
            alt={memberName || "Member Photo"}
            className="w-24 h-24 rounded-full object-cover border-2 border-gray-300 shadow-sm"
          />
        </div>
      )}

      {/* SECTION A: PERSONAL INFORMATION */}
      <SectionTitle>Section A: Personal Information</SectionTitle>
      <LabelValue label="First Name" value={data.first_name} />
      <LabelValue label="Last Name" value={data.last_name} />
      <LabelValue label="Email" value={data.email} />
      <LabelValue label="Phone Number" value={data.phone} />
      <LabelValue label="Date of Birth" value={data.dob} />
      <LabelValue label="Nationality" value={data.nationality} />
      <LabelValue label="ID/Passport Number" value={data.id_passport} />
      <LabelValue label="Residential Address" value={data.residential_address} />
      <LabelValue label="Occupation" value={data.occupation} />
      <LabelValue label="Employer" value={data.employer} />

      {/* SECTION B: MEMBERSHIP DETAILS */}
      <SectionTitle>Section B: Membership Details</SectionTitle>
      <LabelValue label="Membership Type" value={data.member_type?.name || data.membership_type} />
      <LabelValue label="Playing Level" value={data.playing_level?.name || data.playing_level} />
      <LabelValue label="Playing Position" value={data.playing_position?.name || data.playing_position_id} />
      <LabelValue label="Jersey Number" value={data.jersey_number} />

      {/* SECTION C: EMERGENCY CONTACT */}
      <SectionTitle>Section C: Emergency Contact</SectionTitle>
      <LabelValue label="Contact Name" value={data.emergency_contact_name} />
      <LabelValue label="Contact Phone" value={data.emergency_contact_phone} />
      <LabelValue label="Relationship" value={data.emergency_contact_relationship} />

      {/* SECTION D: MEDICAL INFORMATION */}
      <SectionTitle>Section D: Medical Information (Optional but recommended)</SectionTitle>
      <LabelValue label="Medical Info" value={data.medical_info} />
      <LabelValue label="Health Conditions" value={data.health_conditions} />
      <LabelValue label="Allergies" value={data.allergies} />
      <LabelValue label="Medical Conditions" value={data.medical_conditions} />
      <LabelValue label="Insurance" value={data.insurance} />
      <LabelValue label="Insurance Provider" value={data.insurance_provider} />

      {/* SECTION E: CONSENT AND DECLARATION */}
      <SectionTitle>Section E: Consent and Declaration</SectionTitle>
      <div className="mb-2">
        <div className="flex items-center mb-1"><span className="mr-2">{data.terms_accepted ? '☑' : '☐'}</span> I hereby apply for membership in the Old Guards Basketball Club.</div>
        <div className="flex items-center mb-1"><span className="mr-2">{data.consent ? '☑' : '☐'}</span> I agree to abide by the Constitution, Rules, and Code of Conduct of the Club.</div>
        <div className="flex items-center mb-1"><span className="mr-2">{data.consent ? '☑' : '☐'}</span> I understand that participation in club activities is at my own risk and that I am responsible for my personal insurance.</div>
        <div className="flex items-center mb-1"><span className="mr-2">{data.consent ? '☑' : '☐'}</span> I consent to the Club storing and using my personal data in accordance with applicable privacy laws and for club-related communications.</div>
      </div>
      <div className="flex items-center mt-4 mb-8">
        <span className="font-medium mr-2">Signature:</span>
        <span className="border-b border-gray-400 px-8 py-1 font-bold text-lg" style={{ fontFamily: 'cursive' }}>{getInitials(data.first_name, data.last_name)}</span>
      </div>

      {/* SECTION F: CLUB USE ONLY */}
      {showClubSection && (
        <>
          <SectionTitle>Section F: Club Use Only</SectionTitle>
          <div className="mb-1 flex"><span className="w-56 text-gray-700 font-medium">Application Received By:</span> <span className="border-b border-gray-400 flex-1" style={{ minWidth: 120 }}></span></div>
          <div className="mb-1 flex"><span className="w-56 text-gray-700 font-medium">Date Received:</span> <span className="border-b border-gray-400 flex-1" style={{ minWidth: 120 }}></span></div>
          <div className="mb-1 flex items-center"><span className="w-56 text-gray-700 font-medium">Membership Category Approved:</span>
            <span className="ml-2">☐ Full ☐ Associate ☐ Veteran ☐ Honorary</span>
          </div>
          <div className="mb-1 flex"><span className="w-56 text-gray-700 font-medium">Membership ID No.:</span> <span className="border-b border-gray-400 flex-1" style={{ minWidth: 120 }}></span></div>
          <div className="mb-1 flex"><span className="w-56 text-gray-700 font-medium">Approved By (Signature):</span> <span className="border-b border-gray-400 flex-1" style={{ minWidth: 120 }}></span></div>
          <div className="mb-1 flex"><span className="w-56 text-gray-700 font-medium">Date of Approval:</span> <span className="border-b border-gray-400 flex-1" style={{ minWidth: 120 }}></span></div>
        </>
      )}
    </div>
  );
};

export default MemberFormTemplate; 